// Poll/route logic for the booking step when classification is pending
// (social enrichment still running at strategy selection). Pure: the check
// and sleep are injected, so it tests without the generated GraphQL hooks.

import { BookingEligibility } from '../model/Onboarding.types';

export type BookingRoute = 'book-call' | 'guide';

// Enrichment (Apify scrape + webhook processing) can take minutes, and a
// qualifying creator who books the call is worth the wait — so the bound is
// generous. It exists only as a safety valve: past it, the flow falls back
// to the guide and the creator is still classified server-side later.
export const BOOKING_POLL_INTERVAL_MS = 5_000;
const BOOKING_POLL_TIMEOUT_MS = 180_000;

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Route for a resolved classification. Pending has no route — it is the
// "keep waiting" state handled by pollBookingEligibility.
export const toBookingRoute = (
  eligibility: Exclude<BookingEligibility, 'pending'>,
): BookingRoute => (eligibility === 'eligible' ? 'book-call' : 'guide');

type PollBookingEligibilityOptions = {
  check: () => Promise<BookingEligibility>;
  sleep?: (ms: number) => Promise<void>;
  timeoutMs?: number;
  intervalMs?: number;
};

// Re-checks an initially-pending classification on an interval until the
// verdict resolves, falling back to the guide once the timeout elapses.
// The timeout fallback loses nothing permanently: the backend never freezes
// a pending verdict, so the creator stays classifiable server-side later.
export const pollBookingEligibility = async ({
  check,
  sleep = defaultSleep,
  timeoutMs = BOOKING_POLL_TIMEOUT_MS,
  intervalMs = BOOKING_POLL_INTERVAL_MS,
}: PollBookingEligibilityOptions): Promise<BookingRoute> => {
  for (let elapsed = intervalMs; elapsed <= timeoutMs; elapsed += intervalMs) {
    await sleep(intervalMs);

    const eligibility = await check();
    if (eligibility !== 'pending') {
      return toBookingRoute(eligibility);
    }
  }

  return 'guide';
};
